/**
 * ft-inline-helpers.ts — TS port of includes/js/ft-inline-helpers.js (Wave 2a of
 * plans/bundler-script-load-order-phase2.md; the PRE-template-tier fold).
 *
 * Vanilla helpers for inline onclick attributes — minimal DOM ops that replace common jQuery onclick
 * patterns. Dependency-free; loaded BEFORE template.js so inline handlers in PHP-generated HTML always
 * have these globals.
 *
 * MODULE-SCOPE SAFE (clean 2a): eval-time work = ONLY the window attaches (no listeners, no immediate
 * calls, no template.js reads). Every function body runs at onclick / form-interaction time.
 *
 * PORT NOTE — the implicit-global trap: the original was a classic <script> using `window.X = function`,
 * which ALSO created bare global bindings, so selectCharacterSkillsFilter / selectCreatureSkillsFilter
 * could call showEl / hideEl / showByClass(Within) / hideByClass(Within) by BARE NAME. A module's
 * `window.X =` does NOT create a bare binding, so this port declares every helper as a `function`
 * declaration (giving the module-local binding the bare cross-calls need) and attaches them all to
 * window at the bottom — exact public surface, bare cross-calls preserved.
 *
 * External call-time deps: window.bootstrap (BS5 Tooltip, typeof/null-guarded), getComputedStyle (builtin).
 *
 * Pivot ref: [[project_jquery_pivot]] Phase 6.
 */

// Show: clear inline display so CSS rule wins.
function showEl(id: string): void {
    const el = document.getElementById(id);
    if (el) el.style.display = '';
}

// Hide: inline display:none.
function hideEl(id: string): void {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
}

// Toggle visibility. Mirrors jQuery's .toggle() — inspects computed display so an element hidden via CSS class shows on first click.
function toggleEl(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    const hidden = el.style.display === 'none' || getComputedStyle(el).display === 'none';
    el.style.display = hidden ? '' : 'none';
}

// Set .value on any form element (input, select, textarea).
function setVal(id: string, value: unknown): void {
    const el = document.getElementById(id);
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        el.value = String(value ?? '');
    }
}

// Programmatic click trigger.
function clickEl(id: string): void {
    const el = document.getElementById(id) as HTMLElement | null;
    if (el) el.click();
}

// Show all elements matching .class (global scope).
function showByClass(cls: string): void {
    document.querySelectorAll<HTMLElement>('.' + cls).forEach((el) => { el.style.display = ''; });
}

// Hide all elements matching .class (global scope).
function hideByClass(cls: string): void {
    document.querySelectorAll<HTMLElement>('.' + cls).forEach((el) => { el.style.display = 'none'; });
}

// Show all .class descendants of #parentId (scoped).
function showByClassWithin(parentId: string, cls: string): void {
    const parent = document.getElementById(parentId);
    if (parent) parent.querySelectorAll<HTMLElement>('.' + cls).forEach((el) => { el.style.display = ''; });
}

// Hide all .class descendants of #parentId (scoped).
function hideByClassWithin(parentId: string, cls: string): void {
    const parent = document.getElementById(parentId);
    if (parent) parent.querySelectorAll<HTMLElement>('.' + cls).forEach((el) => { el.style.display = 'none'; });
}

// Set .innerHTML (used by short copy-feedback patterns like setHtml('div_x', 'Copied!')).
function setHtml(id: string, html: string): void {
    const el = document.getElementById(id);
    if (el) el.innerHTML = html;
}

// Flash a "Copied!" Bootstrap 5 tooltip on a button (replaces the jQuery share-button pattern
// `$('#id').attr('title','Copied!').tooltip('_fixTitle').tooltip('show')`). Uses BS5's native
// Tooltip; getOrCreateInstance so buttons without data-bs-toggle still get one. No-op if BS absent.
function showCopiedTooltip(id: string): void {
    const el = document.getElementById(id);
    if (!el || !window.bootstrap || !bootstrap.Tooltip) return;
    el.setAttribute('title', 'Copied!');
    el.setAttribute('data-bs-original-title', 'Copied!');
    const tip = bootstrap.Tooltip.getOrCreateInstance(el);
    if (typeof tip.setContent === 'function') tip.setContent({ '.tooltip-inner': 'Copied!' });
    tip.show();
}

/**
 * Wraps a native <input type="number"> with prominent +/- buttons.
 * Vanilla replacement for bootstrap-input-spinner. Honors min, max, step.
 * Hold-to-repeat. Dispatches input + change events so existing onchange handlers fire.
 * Idempotent: marks the input with data-ft-spinner-applied so double-calls are no-ops.
 */
function enhanceNumberInput(idOrElement: string | HTMLInputElement): void {
    const candidate = typeof idOrElement === 'string' ? document.getElementById(idOrElement) : idOrElement;
    const el = candidate instanceof HTMLInputElement ? candidate : null;
    if (!el || el.type !== 'number' || el.dataset.ftSpinnerApplied === '1') return;
    const input = el;
    el.dataset.ftSpinnerApplied = '1';
    el.classList.add('ft-spinner-input');

    const isSmall = el.classList.contains('form-control-sm');
    const groupClass = 'input-group ft-spinner-group' + (isSmall ? ' input-group-sm' : '');
    const btnClass = 'btn btn-outline-secondary ft-spinner-btn' + (isSmall ? ' btn-sm' : '');

    const wrapper = document.createElement('div');
    wrapper.className = groupClass;

    function makeBtn(symbol: string, label: string) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = btnClass;
        b.innerHTML = symbol;
        b.setAttribute('aria-label', label);
        b.tabIndex = -1;
        return b;
    }
    const minusBtn = makeBtn('&minus;', 'Decrement');
    const plusBtn = makeBtn('&plus;', 'Increment');

    if (!el.parentNode) return;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(minusBtn);
    wrapper.appendChild(el);
    wrapper.appendChild(plusBtn);

    function step(direction: number) {
        const stepVal = parseFloat(input.step) || 1;
        let cur = parseFloat(input.value);
        if (isNaN(cur)) cur = 0;
        let next = cur + (direction * stepVal);
        const min = input.min !== '' ? parseFloat(input.min) : -Infinity;
        const max = input.max !== '' ? parseFloat(input.max) : Infinity;
        next = Math.max(min, Math.min(max, next));
        // Avoid floating-point artifacts when step has decimals (e.g. step="0.1").
        const stepStr = String(input.step || '1');
        const precision = (stepStr.split('.')[1] || '').length;
        if (precision > 0) next = parseFloat(next.toFixed(precision));
        input.value = String(next);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    let holdTimer: ReturnType<typeof setTimeout> | null = null;
    let holdInterval: ReturnType<typeof setInterval> | null = null;
    function endHold() {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (holdInterval) { clearInterval(holdInterval); holdInterval = null; }
    }
    function startHold(direction: number) {
        endHold();
        step(direction);
        holdTimer = setTimeout(() => {
            holdInterval = setInterval(() => step(direction), 80);
        }, 400);
    }
    minusBtn.addEventListener('mousedown', e => { e.preventDefault(); startHold(-1); });
    plusBtn.addEventListener('mousedown', e => { e.preventDefault(); startHold(1); });
    ['mouseup', 'mouseleave', 'touchend', 'touchcancel', 'blur'].forEach(ev => {
        minusBtn.addEventListener(ev, endHold);
        plusBtn.addEventListener(ev, endHold);
    });
    // Touch support: prevent simulated mousedown firing twice on mobile.
    minusBtn.addEventListener('touchstart', e => { e.preventDefault(); startHold(-1); }, { passive: false });
    plusBtn.addEventListener('touchstart', e => { e.preventDefault(); startHold(1); }, { passive: false });
}

/**
 * Toggle visibility of a panel and add/remove a set of "open" classes on a sibling header
 * based on the panel's now-visible state. Replaces the jQuery pattern:
 *   var $p=$('#showId'); $p.toggle(); $('#headerId').toggleClass('classes', $p.is(':visible'));
 *
 * classes is a space-separated string (e.g. "pb-2 border-bottom border-light"); each is
 * toggled with the panel's new visibility as the second arg to classList.toggle.
 */
function toggleSectionWithHeader(showId: string, headerId: string, classes: string): void {
    const showElNode = document.getElementById(showId);
    const headerEl = document.getElementById(headerId);
    if (!showElNode || !headerEl) return;
    const wasHidden = showElNode.style.display === 'none' || getComputedStyle(showElNode).display === 'none';
    showElNode.style.display = wasHidden ? '' : 'none';
    const nowVisible = wasHidden;
    classes.split(/\s+/).filter(Boolean).forEach(c => headerEl.classList.toggle(c, nowVisible));
}

/**
 * Character sheet skills tab filter. Scoped to a specific skills panel by dom suffix,
 * because multiple skill panels can live on one page (e.g. multi-character views).
 *
 * mode:
 *   'icons' — show icon grid, hide table
 *   'all'   — hide icons, show table with all rows
 *   '<type>' (defensive, innate, gathering, knowledge, crafting, language,
 *             instrument_classification, weapon, armor)
 *           — hide icons, show table with only that type's rows
 */
function selectCharacterSkillsFilter(suffix: string, mode: string): void {
    const iconsId = 'div_all_skills_icons' + suffix;
    const listId = 'div_table_skill_list' + suffix;
    if (mode === 'icons') {
        showEl(iconsId);
        hideEl(listId);
        return;
    }
    hideEl(iconsId);
    showEl(listId);
    if (mode === 'all') {
        showByClassWithin(listId, 'cl_tr_all_skills');
    } else {
        hideByClassWithin(listId, 'cl_tr_all_skills');
        showByClassWithin(listId, 'cl_tr_' + mode + '_skill');
    }
}

/**
 * Encounter creature skills tab filter. Global class scope (no parent) — mirrors
 * the original jQuery `$('.cl_tr_X_skill')` selectors that affected every creature
 * panel on the page simultaneously.
 *
 * mode:
 *   'all'    — show all-skills rows only
 *   '<type>' — hide all-skills rows, show only that type's rows
 */
function selectCreatureSkillsFilter(mode: string): void {
    if (mode === 'all') {
        showByClass('cl_tr_all_skills');
    } else {
        hideByClass('cl_tr_all_skills');
        showByClass('cl_tr_' + mode + '_skill');
    }
}

// ===========================================================================================
// Window attachment — exact public surface of the original plain-JS file (all 16 were window.X =).
// Declared as `function`s above so the bare cross-calls inside the two skills filters resolve to the
// module-local bindings (the implicit-global a classic <script> gave for free).
// ===========================================================================================
declare global {
    interface Window {
        bootstrap?: FtBootstrapGlobal;
        showEl: typeof showEl;
        hideEl: typeof hideEl;
        toggleEl: typeof toggleEl;
        slideToggleEl: typeof toggleEl;
        setVal: typeof setVal;
        clickEl: typeof clickEl;
        showByClass: typeof showByClass;
        hideByClass: typeof hideByClass;
        showByClassWithin: typeof showByClassWithin;
        hideByClassWithin: typeof hideByClassWithin;
        setHtml: typeof setHtml;
        showCopiedTooltip: typeof showCopiedTooltip;
        enhanceNumberInput: typeof enhanceNumberInput;
        toggleSectionWithHeader: typeof toggleSectionWithHeader;
        selectCharacterSkillsFilter: typeof selectCharacterSkillsFilter;
        selectCreatureSkillsFilter: typeof selectCreatureSkillsFilter;
    }
}

window.showEl = showEl;
window.hideEl = hideEl;
window.toggleEl = toggleEl;
// slideToggleEl: same show/hide as toggleEl (no animation). Filter panels do not visually depend on the slide.
window.slideToggleEl = toggleEl;
window.setVal = setVal;
window.clickEl = clickEl;
window.showByClass = showByClass;
window.hideByClass = hideByClass;
window.showByClassWithin = showByClassWithin;
window.hideByClassWithin = hideByClassWithin;
window.setHtml = setHtml;
window.showCopiedTooltip = showCopiedTooltip;
window.enhanceNumberInput = enhanceNumberInput;
window.toggleSectionWithHeader = toggleSectionWithHeader;
window.selectCharacterSkillsFilter = selectCharacterSkillsFilter;
window.selectCreatureSkillsFilter = selectCreatureSkillsFilter;

export {};
